// close_out_permit (WF-018/019, CORE-072). The RS (the AA on sandboxes before 0016) inspects the worksite after the PH
// cancellation and closes the permit out. The server then queues the archive copy.
import { useId, useState } from 'react'
import { RuleRefs, SignatureLine } from '@/components/evidence'
import { Banner, Button, Card, Field, KeyValue, RadioGroup, TextArea, TextInput } from '@/components/ui'
import { AA_CLOSEOUT_CHECKS, ISOLATION_DISPOSITIONS, ISOLATION_KINDS } from '@/domain/catalogue'
import { labelFor } from '@/lib/format'
import type { ActionPanelProps } from '@/features/contracts'
import { useCommand } from '@/lib/queries'
import {
  buildCloseoutArgs,
  cancellationReasonLabel,
  closeoutHints,
  closeoutUnanswered,
  dispositionNeedsNote,
  expectedVersionFor,
  initialCloseoutInput,
  permitIsolationSummary,
  permitNeedsIsolation,
  phWorkStatus,
  type CloseoutInput,
  type WorkStatus,
} from '../logic'
import { CommandFailure, HintList, NotAvailable, PanelFooter, Question, SigningStatement, Tick, TickList, Unanswered, YesNoBadge } from '../parts'

const WORK_STATUS_OPTIONS: { value: WorkStatus; label: string }[] = [
  { value: 'complete', label: 'Complete' },
  { value: 'incomplete', label: 'Incomplete' },
]

export function CloseoutPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const cmd = useCommand('close_out_permit')
  const findingsId = useId()
  const operationId = useId()
  const groupId = useId()
  const noteId = useId()
  const [input, setInput] = useState<CloseoutInput>(() => initialCloseoutInput(bundle))

  const cancellation = bundle.cancellation
  // The role the server offered: RS from 0016 (AA only on sandboxes before it).
  const signingAs = action.role === 'AA' ? 'Area Authority' : 'Responsible Supervisor'
  const phStatus = phWorkStatus(cancellation)
  const needsIsolation = permitNeedsIsolation(bundle)
  const isolation = permitIsolationSummary(bundle)
  const missing = closeoutUnanswered(input, needsIsolation)
  const hints = closeoutHints(input, phStatus)

  const edit = (patch: Partial<CloseoutInput>) => {
    // Changed input is a new intent: never resend it under the old request key.
    if (cmd.error) cmd.resetIntent()
    setInput((prev) => ({ ...prev, ...patch }))
  }

  const submit = async () => {
    const args = buildCloseoutArgs(bundle.permit.id, input, needsIsolation)
    if (!args) return
    try {
      await cmd.run(args, expectedVersionFor('close_out_permit', bundle))
      onDone()
    } catch {
      /* shown by CommandFailure */
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink">
        Inspect the worksite, then record what you found. Closing out does not need a current validation or gas test.
        <RuleRefs ruleIds={['CORE-072']} />
      </p>

      <NotAvailable action={action} />

      {cancellation ? (
        <Card>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">What the Permit Holder signed</p>
          <KeyValue
            items={[
              ['Reason', cancellationReasonLabel(cancellation.reason_code)],
              ['Work', cancellation.work_complete ? 'Complete' : 'Incomplete'],
              [
                'Work continues on',
                <span className="whitespace-pre-wrap">
                  {cancellation.continuation_reference ?? (cancellation.work_complete ? 'Not applicable (work complete)' : 'Not stated')}
                </span>,
              ],
              ['Tools and equipment removed', <YesNoBadge value={cancellation.tools_equipment_removed} />],
              ['Worksite condition', <span className="whitespace-pre-wrap">{cancellation.worksite_condition}</span>],
            ]}
          />
          <div className="mt-3">
            <SignatureLine a={cancellation.attestation} label="Permit Holder cancellation" />
          </div>
        </Card>
      ) : (
        <Banner tone="warn" title="No PH cancellation on this permit">
          The Responsible Supervisor closes out only after the Permit Holder has signed the cancellation.
        </Banner>
      )}

      <Question legend="Your checks" required hint="Tick each one only if you did it.">
        <TickList options={AA_CLOSEOUT_CHECKS} value={input.checks} onChange={(checks) => edit({ checks })} />
      </Question>

      <div className="rounded-lg border border-line bg-white px-3">
        <Tick checked={input.site_safe_confirmed} onChange={(v) => edit({ site_safe_confirmed: v })} label="I confirm the site is safe." />
      </div>

      <Question
        legend="Work status you confirm"
        required
        hint={
          phStatus
            ? `Filled in from the PH statement (${phStatus}). It must match what the PH signed.`
            : 'Choose what you found.'
        }
      >
        <RadioGroup<WorkStatus>
          name={`${groupId}-work-status`}
          value={input.work_status_confirmed}
          onChange={(v) => edit({ work_status_confirmed: v })}
          options={WORK_STATUS_OPTIONS}
        />
      </Question>

      <HintList hints={hints} />

      <Field label="Inspection findings" required htmlFor={findingsId} hint="What you found at the worksite.">
        <TextArea
          id={findingsId}
          value={input.inspection_findings}
          onChange={(e) => edit({ inspection_findings: e.target.value })}
          placeholder="e.g. Supports SYN-PS-101 to 103 cleaned, area clear, no tools left, barriers removed"
        />
      </Field>

      <Field label="Operation check (where required)" htmlFor={operationId} hint="How you checked the equipment is fit to operate, if a check was needed. Leave empty if none was needed.">
        <TextArea id={operationId} value={input.operation_check} onChange={(e) => edit({ operation_check: e.target.value })} />
      </Field>

      {needsIsolation && isolation ? (
        <Question
          legend="What became of the isolation this permit needed?"
          required
          hint={`Declared on the application: ${isolation.kinds.map((k) => labelFor(ISOLATION_KINDS, k)).join(', ') || 'kind not stated'}${isolation.certificate_reference ? `; Isolation Certificate No. ${isolation.certificate_reference}` : ''}. Closing out never removes an isolation by itself.`}
        >
          <RadioGroup<string>
            name={`${groupId}-isolation`}
            value={input.isolation_disposition}
            onChange={(v) => edit({ isolation_disposition: v, ...(dispositionNeedsNote(v) ? {} : { isolation_disposition_note: '' }) })}
            options={ISOLATION_DISPOSITIONS.map((d) => ({ value: d.code, label: d.label }))}
          />
          {dispositionNeedsNote(input.isolation_disposition) ? (
            <div className="mt-2">
              <Field
                label={input.isolation_disposition === 'transferred' ? 'Permit the isolation was transferred to' : 'EPI reference (F09 register)'}
                required
                htmlFor={noteId}
              >
                <TextInput id={noteId} value={input.isolation_disposition_note} onChange={(e) => edit({ isolation_disposition_note: e.target.value })} />
              </Field>
            </div>
          ) : null}
          <RuleRefs ruleIds={['CORE-072', 'ACT-015', 'ACT-019']} />
        </Question>
      ) : (
        <KeyValue items={[['Isolations', 'None - this permit needed no isolation']]} />
      )}

      <SigningStatement>
        <p>
          You sign as <strong>{signingAs}</strong> that you inspected the worksite, did each check you ticked, that the site is
          safe, and that the work is {input.work_status_confirmed ?? 'complete or incomplete (your answer above)'}.
        </p>
        <p>
          This closes the permit and starts the archive copy. The person closing out cannot be the Permit Holder on the same permit.
        </p>
      </SigningStatement>

      <CommandFailure error={cmd.error} />
      <Unanswered items={missing} />

      <PanelFooter>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => void submit()} loading={cmd.isPending} disabled={missing.length > 0 || !action.enabled}>
          Sign closeout as {signingAs}
        </Button>
      </PanelFooter>
    </div>
  )
}
